import { setTimeout as delay } from "node:timers/promises";
import type {
  GraphState,
  InterruptInput,
  InterruptResumeValue,
  NodeConfig,
  NodeResult,
  WorkflowDefinition,
} from "@kortyx/core";
import {
  assertExecutionBudget,
  consumeExecutionBudget,
  isExecutionCancelled,
  isExecutionLimitReached,
  throwIfExecutionAborted,
} from "@kortyx/core";
import { KortyxError, WorkflowContractError } from "@kortyx/core/errors";
import type { KortyxTelemetryConfig, ReasonTraceAdapter } from "@kortyx/hooks";
import { runWithHookContext } from "@kortyx/hooks";
import {
  safeTelemetryMetadata,
  withSafeTraceSpan,
} from "@kortyx/hooks/internal";
import type { GetProviderFn } from "@kortyx/providers";
import { deepMergeWithArrayOverwrite, mergeRuntimeState } from "@kortyx/utils";
import {
  Annotation,
  interrupt,
  isGraphInterrupt,
  StateGraph,
} from "@langchain/langgraph";
import type { BaseCheckpointSaver } from "@langchain/langgraph-checkpoint";
import { getCheckpointer } from "../checkpointer";
import {
  acknowledgeResumeDelivery,
  type ResumeDelivery,
} from "../framework/graph-snapshot";
import { resolveNodeHandler } from "../node-loader";
import { createWorkflowCallService } from "./call-workflow";
import { copyReducedFields, mergeWorkflowData } from "./data";
import {
  addParallelGraphCoordinator,
  hasParallelEdges,
  type NodeExecutor,
} from "./parallel-graph";

interface CompiledGraphBase {
  invoke(state: unknown, options?: Record<string, unknown>): Promise<unknown>;
  streamEvents(
    state: GraphState,
    options?: { version?: string; configurable?: Record<string, unknown> },
  ): AsyncIterable<unknown> | AsyncGenerator<unknown>;
}

export type ExecutionControl = {
  /** Internal: Response delivery belongs to the current invocation, not configuration. */
  resumeDelivery?: ResumeDelivery | undefined;
  completeResponse?:
    | ((
        options: import("@kortyx/hooks").CompleteResponseOptions,
        state: GraphState,
      ) => Promise<void>)
    | undefined;
  abortSignal?: AbortSignal | undefined;
  budget?: import("@kortyx/core").ExecutionBudget | undefined;
};

interface GraphExtensions {
  execution: ExecutionControl;
  name: string;
  config: ExecutionRuntimeConfig;
  resume(state: GraphState, input: unknown): Promise<GraphState>;
  timeTravelTo(state: GraphState, checkpointId: string): Promise<GraphState>;
}

interface InterruptResultWithId {
  id: string;
}

interface HookPatchError {
  __kortyxHookStatePatch?: Record<string, unknown> | null;
}

export interface ExecutionRuntimeConfig {
  prompts?: import("@kortyx/prompts").PromptExecution | undefined;
  executionRunId?: string | undefined;
  executionBranchId?: string | undefined;
  prepareChildTelemetry?:
    | ((
        workflow: WorkflowDefinition,
        config: ExecutionRuntimeConfig,
      ) => ExecutionRuntimeConfig)
    | undefined;
  selectWorkflow?: ((id: string) => Promise<WorkflowDefinition>) | undefined;
  workflowCallDepth?: number;
  invocationPath?: string;
  emit?: (event: string, payload: unknown) => void;
  onCheckpoint?: (args: { nodeId: string; state: GraphState }) => void;
  checkpointer?: BaseCheckpointSaver;
  reasonTrace?: ReasonTraceAdapter | undefined;
  telemetry?: KortyxTelemetryConfig | undefined;
  getProvider?: GetProviderFn | undefined;
  [key: string]: unknown;
}

export async function createExecutionGraph(
  workflow: WorkflowDefinition,
  config: ExecutionRuntimeConfig,
  execution: ExecutionControl = {},
) {
  if (workflow.reduce && !workflow.outputSchema)
    throw new WorkflowContractError("Workflow reducers require outputSchema.");
  const StateAnnotation = Annotation.Root({
    currentWorkflow: Annotation<string>,
    input: Annotation<unknown>,
    output: Annotation<string>,
    lastNode: Annotation<string>,
    lastCondition: Annotation<string>,
    lastIntent: Annotation<string>,
    runtime: Annotation<Record<string, unknown>>({
      reducer: (l, r) => mergeRuntimeState(l ?? {}, r ?? {}),
      default: () => ({}),
    }),
    config: Annotation<unknown>,
    transitionTo: Annotation<string | null>,
    data: Annotation<Record<string, unknown>>({
      // Reducer-enabled nodes emit complete snapshots, including removed fields.
      // Loading a checkpoint/snapshot must never invoke application reducers.
      reducer: (l, r) =>
        workflow.reduce ? r : deepMergeWithArrayOverwrite(l ?? {}, r ?? {}),
      default: () => ({}),
    }),
    conversationHistory: Annotation<
      Array<{ node: string; message: string; timestamp: string }>
    >({
      reducer: (l, r) => [...(l ?? []), ...(r ?? [])],
      default: () => [],
    }),
    awaitingHumanInput: Annotation<boolean>,
  });

  const builder = new StateGraph(StateAnnotation);
  const edgeApi = builder as unknown as {
    addEdge: (from: string, to: string) => void;
    addConditionalEdges: (
      from: string,
      fn: (s: GraphState) => string,
      mapping: Record<string, string>,
    ) => void;
    compile: (args: { checkpointer: BaseCheckpointSaver }) => CompiledGraphBase;
  };
  const workflowName = workflow.id;
  const parallelGraph = hasParallelEdges(workflow);
  const nodeExecutors: Record<string, NodeExecutor> = {};

  // Ensure an emit function exists so internal hook plumbing can emit events.
  type WithEmit = ExecutionRuntimeConfig & {
    emit: (event: string, payload: unknown) => void;
  };
  const runtimeConfig = config as WithEmit;
  if (!runtimeConfig.emit) {
    runtimeConfig.emit = () => {};
  }

  const isRecord = (value: unknown): value is Record<string, unknown> =>
    Boolean(value) && typeof value === "object" && !Array.isArray(value);

  const traceAdapter = (): ReasonTraceAdapter | undefined =>
    runtimeConfig.telemetry?.trace ?? runtimeConfig.reasonTrace;

  const telemetryCorrelation = () => runtimeConfig.telemetry?.correlation;

  const configContext = (): Record<string, unknown> | undefined =>
    isRecord(runtimeConfig.context) ? runtimeConfig.context : undefined;

  const toRecordInput = (value: unknown): Record<string, unknown> => {
    if (isRecord(value)) return value;
    if (value === undefined) return {};
    return { rawInput: value };
  };

  const buildNodeConfig = (
    params: Record<string, unknown> | undefined,
    nodeBehavior: Record<string, unknown> | undefined,
  ): NodeConfig => {
    const model = isRecord(params?.model)
      ? (params.model as NodeConfig["model"])
      : undefined;
    const tool = isRecord(params?.tool)
      ? (params.tool as NodeConfig["tool"])
      : undefined;
    const behaviorFromParams = isRecord(params?.behavior)
      ? (params.behavior as NodeConfig["behavior"])
      : undefined;
    const behavior: NonNullable<NodeConfig["behavior"]> = {
      ...(behaviorFromParams ?? {}),
      ...(nodeBehavior ?? {}),
    };
    const options = params ?? undefined;
    return {
      ...(model ? { model } : {}),
      ...(tool ? { tool } : {}),
      ...(Object.keys(behavior).length > 0 ? { behavior } : {}),
      ...(options ? { options } : {}),
    } as NodeConfig;
  };

  for (const [nodeId, nodeDef] of Object.entries(workflow.nodes ?? {})) {
    const resolvedRun = await resolveNodeHandler({
      run: nodeDef.run,
      workflow,
    });
    const nodeParams = (nodeDef.params ?? undefined) as
      | Record<string, unknown>
      | undefined;
    const nodeConfig: NodeConfig = buildNodeConfig(
      nodeParams,
      (nodeDef.behavior ?? undefined) as unknown as
        | Record<string, unknown>
        | undefined,
    );
    const behavior = nodeConfig.behavior ?? {};

    const executeNode: NodeExecutor = async (
      state,
      awaitInput,
      snapshotGraph,
    ) => {
      throwIfExecutionAborted(execution.abortSignal);
      const emitRuntimeEvent = (event: string, payload: unknown) => {
        runtimeConfig.emit(event, payload);
      };
      if (awaitInput)
        emitRuntimeEvent("status", {
          node: nodeId,
          message: `Processing node: ${nodeId}`,
        });
      let suspension: unknown;
      let deniedAdmission: unknown;
      let interruptPosition = 0;
      const hookNodeContext = {
        completeResponse: runtimeConfig.workflowCallDepth
          ? undefined
          : execution.completeResponse
            ? async (
                options: import("@kortyx/hooks").CompleteResponseOptions,
                nodeState: GraphState,
              ) => {
                await execution.completeResponse?.(
                  options,
                  snapshotGraph ? snapshotGraph(nodeState) : nodeState,
                );
              }
            : undefined,
        abortSignal: execution.abortSignal,
        consumeExecution: (limit: import("@kortyx/core").ExecutionLimit) => {
          throwIfExecutionAborted(execution.abortSignal);
          if (suspension) throw suspension;
          try {
            if (execution.budget)
              consumeExecutionBudget(execution.budget, limit);
          } catch (error) {
            deniedAdmission = error;
            throw error;
          }
        },
        workflowCallTelemetry: runtimeConfig.telemetry
          ? {
              ...runtimeConfig.telemetry,
              correlation: {
                ...runtimeConfig.telemetry.correlation,
                runId: runtimeConfig.executionRunId,
                branchId:
                  runtimeConfig.executionBranchId ??
                  runtimeConfig.executionRunId,
              },
            }
          : undefined,
        ...(runtimeConfig.selectWorkflow
          ? {
              callWorkflow: createWorkflowCallService(
                {
                  ...runtimeConfig,
                  context: state.config?.context ?? runtimeConfig.context,
                },
                workflow,
                execution,
              ),
            }
          : {}),
        graph: { name: workflowName, node: nodeId },
        config: nodeConfig,
        emit: emitRuntimeEvent,
        replayInterrupt: (request: InterruptInput): void => {
          throwIfExecutionAborted(execution.abortSignal);
          // Both LangGraph and the parallel coordinator consume responses by
          // position. Durable hooks skip completed work, but must still advance
          // those response slots before asking their next question.
          if (awaitInput) awaitInput(request);
          else interrupt(request);
          acknowledgeResumeDelivery(
            execution.resumeDelivery,
            nodeId,
            interruptPosition++,
          );
        },
        awaitInterrupt: (
          interruptConfig: InterruptInput,
        ): InterruptResumeValue => {
          throwIfExecutionAborted(execution.abortSignal);
          if (execution.budget) assertExecutionBudget(execution.budget);
          const position = interruptPosition++;
          const { kind, question } = interruptConfig;
          const isCustom = kind === "custom";
          const isMulti =
            kind === "multi-choice" ||
            (interruptConfig.kind === "choice" &&
              interruptConfig.multiple === true);
          const sharedInterruptFields = {
            ...(typeof interruptConfig.id === "string" &&
            interruptConfig.id.length > 0
              ? { id: interruptConfig.id }
              : {}),
            ...(typeof interruptConfig.schemaId === "string" &&
            interruptConfig.schemaId.length > 0
              ? { schemaId: interruptConfig.schemaId }
              : {}),
            ...(typeof interruptConfig.schemaVersion === "string" &&
            interruptConfig.schemaVersion.length > 0
              ? { schemaVersion: interruptConfig.schemaVersion }
              : {}),
            ...(interruptConfig.meta &&
            typeof interruptConfig.meta === "object" &&
            !Array.isArray(interruptConfig.meta)
              ? { meta: interruptConfig.meta }
              : {}),
          };
          const payload: InterruptInput = isCustom
            ? {
                kind: "custom",
                request: interruptConfig.request,
                ...(question ? { question } : {}),
                ...(interruptConfig.contract
                  ? { contract: interruptConfig.contract }
                  : {}),
                schemaId: interruptConfig.schemaId,
                schemaVersion: interruptConfig.schemaVersion,
                ...sharedInterruptFields,
              }
            : interruptConfig.kind === "text"
              ? {
                  kind: "text",
                  ...(question ? { question } : {}),
                  ...sharedInterruptFields,
                }
              : {
                  kind: interruptConfig.kind,
                  multiple: isMulti,
                  question: interruptConfig.question,
                  options: interruptConfig.options,
                  ...sharedInterruptFields,
                };
          let resumed: unknown;
          payload.meta = {
            ...payload.meta,
            __kortyxInterruptOccurrence: { nodeId, position },
          };
          try {
            resumed = awaitInput ? awaitInput(payload) : interrupt(payload);
            acknowledgeResumeDelivery(
              execution.resumeDelivery,
              nodeId,
              position,
            );
          } catch (error) {
            suspension = error;
            if (!awaitInput)
              emitRuntimeEvent("interrupt", {
                node: nodeId,
                workflow: workflowName,
                input: payload,
              });
            throw error;
          }
          if (isCustom) return resumed;
          if (isMulti) {
            if (Array.isArray(resumed)) {
              return (resumed as unknown[])
                .map((v) =>
                  typeof v === "string"
                    ? v
                    : v &&
                        typeof v === "object" &&
                        "id" in v &&
                        typeof (v as InterruptResultWithId).id === "string"
                      ? (v as InterruptResultWithId).id
                      : "",
                )
                .filter(Boolean);
            }
            if (typeof resumed === "string") return [resumed];
            return [];
          }
          if (typeof resumed === "string") return resumed;
          if (
            resumed &&
            typeof resumed === "object" &&
            "id" in resumed &&
            typeof (resumed as InterruptResultWithId).id === "string"
          ) {
            return String((resumed as InterruptResultWithId).id);
          }
          return "";
        },
      };

      if (behavior.checkpoint && config?.onCheckpoint) {
        config.onCheckpoint({ nodeId, state });
      }

      const maxAttempts = behavior.retry?.maxAttempts ?? 1;
      let attempt = 0;
      let nodeResult: NodeResult | undefined;
      let hookRuntimeUpdates: Record<string, unknown> | null = null;
      let retryHookRuntime: Record<string, unknown> | null = null;

      while (attempt < maxAttempts) {
        try {
          throwIfExecutionAborted(execution.abortSignal);
          attempt++;
          const attemptState =
            retryHookRuntime && typeof retryHookRuntime === "object"
              ? ({
                  ...state,
                  runtime: mergeRuntimeState(
                    (state.runtime ?? {}) as Record<string, unknown>,
                    retryHookRuntime,
                  ),
                } as GraphState)
              : state;
          const activeTrace = traceAdapter();
          const hookContext = {
            node: { ...hookNodeContext, prompts: config.prompts },
            state: attemptState,
            reasonTrace: activeTrace,
          };
          const runNode = () =>
            runWithHookContext(hookContext, async () => {
              try {
                hookNodeContext.consumeExecution("maxNodeExecutions");
                const result = await resolvedRun({
                  input: state.input,
                  params: (nodeParams ?? {}) as Record<string, unknown>,
                });
                throwIfExecutionAborted(execution.abortSignal);
                // A sibling may exhaust the shared budget while this admitted
                // node completes. Preserve its result; only its own denial
                // prevents a caught limit from committing fallback output.
                if (deniedAdmission) throw deniedAdmission;
                if (suspension) throw suspension;
                return result;
              } catch (error) {
                throwIfExecutionAborted(execution.abortSignal);
                if (deniedAdmission) throw deniedAdmission;
                throw error;
              }
            });
          const context = configContext();
          const spanArgs = {
            name: "kortyx.node",
            attributes: {
              ...(typeof telemetryCorrelation()?.runId === "string"
                ? { runId: telemetryCorrelation()?.runId }
                : {}),
              ...(typeof telemetryCorrelation()?.sessionId === "string"
                ? { sessionId: telemetryCorrelation()?.sessionId }
                : {}),
              workflowId: workflowName,
              ...(typeof telemetryCorrelation()?.workflowRevisionId === "string"
                ? {
                    workflowRevisionId:
                      telemetryCorrelation()?.workflowRevisionId,
                  }
                : {}),
              ...(typeof telemetryCorrelation()?.topologyHash === "string"
                ? { topologyHash: telemetryCorrelation()?.topologyHash }
                : {}),
              invocationId: runtimeConfig.telemetry?.correlation?.invocationId,
              branchId:
                runtimeConfig.executionBranchId ?? runtimeConfig.executionRunId,
              nodeId,
              attempt,
              ...(typeof context?.userId === "string"
                ? { userId: context.userId }
                : {}),
              ...(typeof context?.tenantId === "string"
                ? { tenantId: context.tenantId }
                : {}),
            },
            telemetry: {
              metadata: safeTelemetryMetadata({
                ...(runtimeConfig.telemetry?.metadata ?? {}),
              }),
              tags: runtimeConfig.telemetry?.tags,
              captureContent: runtimeConfig.telemetry?.captureContent,
            },
          };
          const hookRun = await withSafeTraceSpan(
            activeTrace,
            spanArgs,
            runNode,
          );
          nodeResult = hookRun.result as NodeResult;
          hookRuntimeUpdates = hookRun.runtimeUpdates;
          break;
        } catch (err) {
          const patch = (err as HookPatchError)?.__kortyxHookStatePatch as
            | Record<string, unknown>
            | null
            | undefined;
          if (patch && typeof patch === "object") {
            // Preserve hook runtime state even when execution pauses via interrupt.
            // Without this merge, checkpointed hook state (e.g. useReason drafts)
            // can be lost before resume.
            state.runtime = mergeRuntimeState(
              (state.runtime ?? {}) as Record<string, unknown>,
              patch,
            );
            retryHookRuntime = mergeRuntimeState(
              (retryHookRuntime ?? {}) as Record<string, unknown>,
              patch,
            );
          }
          throwIfExecutionAborted(execution.abortSignal);
          if (isExecutionLimitReached(err)) {
            Object.assign(err, { nodeId, workflowId: workflowName });
            throw err;
          }
          if (isExecutionCancelled(err) || isGraphInterrupt(err)) throw err;
          const hasMore = attempt < maxAttempts;
          const delayMs = behavior.retry?.delayMs ?? 0;
          if (hasMore && delayMs > 0) {
            await delay(delayMs, undefined, { signal: execution.abortSignal });
            continue;
          }
          if (!hasMore) throw err;
        }
      }

      throwIfExecutionAborted(execution.abortSignal);
      const res: NodeResult = (nodeResult ?? {}) as NodeResult;
      if (hookRuntimeUpdates) {
        res.infra = {
          ...(res.infra ?? {}),
          runtime: mergeRuntimeState(
            ((res.infra?.runtime ?? {}) as Record<string, unknown>) || {},
            hookRuntimeUpdates,
          ),
        };
      }
      const uiMessage = res.ui?.message;
      const shouldRespond =
        typeof uiMessage === "string" && uiMessage.trim().length > 0;

      if (shouldRespond) {
        emitRuntimeEvent("message", { node: nodeId, content: uiMessage });
      }

      if (res.transitionTo) {
        if (awaitInput)
          throw Object.assign(
            new KortyxError(
              "GRAPH_BRANCH_HANDOFF",
              "transitionTo cannot hand off from a parallel branch; useWorkflow returns to its caller.",
              {
                category: "workflow",
                retryable: false,
                safeMessage:
                  "transitionTo cannot hand off from a parallel branch; useWorkflow returns to its caller.",
              },
            ),
            { __kortyxHookStatePatch: hookRuntimeUpdates },
          );
        emitRuntimeEvent("transition", {
          node: nodeId,
          workflow: workflowName,
          transitionTo: res.transitionTo,
          payload: res.data ?? {},
        });
      }

      const updates: Record<string, unknown> = {};

      if (typeof res.condition === "string")
        updates.lastCondition = res.condition;
      if (typeof res.intent === "string") updates.lastIntent = res.intent;

      if (res.data && typeof res.data === "object") {
        // Treat node `data` as the flowing payload between nodes.
        // Accumulate by merging into the previous input (if any).
        const data = mergeWorkflowData(
          (state.data ?? {}) as Record<string, unknown>,
          res.data as Record<string, unknown>,
          workflow,
        );
        updates.data = data;
        updates.input = copyReducedFields(
          deepMergeWithArrayOverwrite(toRecordInput(state.input), res.data),
          data,
          res.data,
          workflow,
        );
      }
      if (res.infra?.runtime && typeof res.infra.runtime === "object") {
        updates.runtime = mergeRuntimeState(
          (state.runtime ?? {}) as Record<string, unknown>,
          res.infra.runtime as Record<string, unknown>,
        );
      }

      if (res.ui) {
        updates.ui = {
          message:
            typeof res.ui.message === "string"
              ? res.ui.message
              : (state.ui?.message ?? ""),
          structured: deepMergeWithArrayOverwrite(
            (state.ui?.structured ?? {}) as Record<string, unknown>,
            (res.ui.structured ?? {}) as Record<string, unknown>,
          ),
        };
      }

      if (shouldRespond && uiMessage) {
        updates.conversationHistory = [
          ...(state.conversationHistory ?? []),
          {
            node: nodeId,
            message: uiMessage,
            timestamp: new Date().toISOString(),
          },
        ];
      }

      if (res.ui?.structured) {
        const payload = res.ui.structured as Record<string, unknown>;
        const inferredType =
          payload && typeof payload === "object" && "jobs" in payload
            ? "jobs"
            : typeof (payload as { summary?: unknown }).summary === "string" ||
                (payload as { topJobs?: unknown }).topJobs
              ? "summary"
              : "generic";
        emitRuntimeEvent("structured_data", {
          node: nodeId,
          dataType: inferredType,
          data: payload,
        });
      }

      if (awaitInput) updates.__kortyxNodeData = res.data ?? {};
      if (awaitInput)
        emitRuntimeEvent("status", {
          node: nodeId,
          message: `✅ Completed node: ${nodeId}`,
        });
      return updates;
    };
    Object.defineProperty(nodeExecutors, nodeId, {
      value: executeNode,
      enumerable: true,
    });
    if (!parallelGraph)
      builder.addNode(nodeId, (state: GraphState) => executeNode(state));
  }

  type EdgeTriple = readonly [string, string, { when?: string }?];
  const condGroups: Record<string, Array<{ when: string; to: string }>> = {};
  const plainEdges: Array<[string, string]> = [];

  for (const edge of workflow.edges as unknown as EdgeTriple[]) {
    const [from, to, condition] = edge as unknown as EdgeTriple;
    if (condition && typeof condition.when === "string" && condition.when) {
      if (!condGroups[from]) condGroups[from] = [];
      condGroups[from].push({ when: condition.when, to });
    } else {
      plainEdges.push([from, to]);
    }
  }

  if (parallelGraph) {
    addParallelGraphCoordinator(
      builder as unknown as Parameters<typeof addParallelGraphCoordinator>[0],
      workflow,
      runtimeConfig,
      execution,
      nodeExecutors,
    );
  } else {
    for (const [from, to] of plainEdges) {
      edgeApi.addEdge(from, to);
    }

    for (const [from, rules] of Object.entries(condGroups)) {
      const mapping: Record<string, string> = Object.fromEntries(
        rules.map((r) => [r.when, r.to]),
      );
      const fallbackKey = `__default__:${from}`;
      mapping[fallbackKey] = "__end__";
      edgeApi.addConditionalEdges(
        from,
        (s: GraphState) => {
          const trigger = s.lastCondition ?? s.lastIntent;
          if (trigger && Object.hasOwn(mapping, trigger as string)) {
            return trigger as string;
          }
          return fallbackKey;
        },
        mapping,
      );
    }
  }

  const sessionId =
    typeof config.session === "object" &&
    config.session &&
    "id" in config.session &&
    typeof config.session.id === "string"
      ? config.session.id
      : "__default__";
  const checkpointer = config.checkpointer ?? getCheckpointer(sessionId);
  const graph = edgeApi.compile({ checkpointer });

  type RuntimeGraph = typeof graph & GraphExtensions;

  const runtimeGraph = graph as RuntimeGraph;
  runtimeGraph.name = workflow.id;
  runtimeGraph.config = config;
  runtimeGraph.execution = execution;

  runtimeGraph.resume = async (state: GraphState, input: unknown) => {
    const resumed = { ...state, input, awaitingHumanInput: false };
    return (await graph.invoke(resumed)) as GraphState;
  };

  runtimeGraph.timeTravelTo = async (
    state: GraphState,
    checkpointId: string,
  ) => {
    const checkpoints = (
      state.runtime as { checkpoints?: Record<string, { snapshot: unknown }> }
    )?.checkpoints;
    const snapshot = checkpoints?.[checkpointId];
    if (!snapshot) throw new Error(`Checkpoint '${checkpointId}' not found`);
    return graph.invoke(
      snapshot.snapshot as unknown as Partial<
        (typeof StateAnnotation)["State"]
      >,
    ) as unknown as GraphState;
  };

  return runtimeGraph;
}
